import React, { createContext, useState, useContext, ReactNode } from 'react';
import { permanentLayers } from '../data/layersConfig';

// Define satellite mode options
export type SatelliteMode = 'off' | 'standard' | 'streets' | 'high-res';

// Define basemap options
export type BasemapType = 'mapbox-streets' | 'ign-1993' | 'mapbox-outdoors' | 'satellite-a' | 'satellite-b' | 'satellite-c' | 'satellite-enhanced' | 'gee-sentinel-2' | 'gee-landsat-8' | 'gee-landsat-9' | 'gee-modis' | 'gee-spot' | 'gee-sentinel-3' | 'gee-sentinel-6' | 'gee-landsat-10' | 'gee-modis-terra-2025' | 'gee-modis-aqua-2025' | 'gee-planet-skybox' | 'gee-blacksky-constellation' | 'maxar-worldview' | 'airbus-pleiades' | 'planet-skybox' | 'blacksky-constellation' | 'capella-sar' | 'iceye-sar' | 'sentinel-3' | 'sentinel-6' | 'landsat-10' | 'modis-terra-2025' | 'modis-aqua-2025';

// Define the shape of the context data
interface MapContextType {
  satelliteOn: boolean;
  setSatelliteOn: (on: boolean) => void;
  satelliteMode: SatelliteMode;
  setSatelliteMode: (mode: SatelliteMode) => void;
  basemap: BasemapType;
  setBasemap: (basemap: BasemapType) => void;
  visibleLayers: string[];
  toggleLayer: (layerId: string) => void;
  labelsVisible: boolean;
  setLabelsVisible: (visible: boolean) => void;
}

// Create the context with a default undefined value
const MapContext = createContext<MapContextType | undefined>(undefined);

// Create a provider component

export const MapProvider = ({ children }: { children: ReactNode }) => {
  const [satelliteOn, setSatelliteOn] = useState(false);
  const [satelliteMode, setSatelliteMode] = useState<SatelliteMode>('streets'); // Default to streets mode for best UX
  const [basemap, setBasemap] = useState<BasemapType>('mapbox-streets'); // Default to Mapbox Streets
  const [labelsVisible, setLabelsVisible] = useState(true); // Labels on by default
  const [visibleLayers, setVisibleLayers] = useState<string[]>(() => {
    // Ensure couloirs is always visible by default
    const defaultVisible = permanentLayers
      .filter((layer: any) => layer.visible)
      .map((layer: any) => layer.id);
    
    // Make sure couloirs is included
    if (!defaultVisible.includes('couloirs')) {
      defaultVisible.push('couloirs');
    }
    
    // Handle commercial zones - if parent is visible, add children
    if (defaultVisible.includes('zones-commerciales')) {
      defaultVisible.push('zone-commerciale-chami', 'zone-commerciale-sfariyatt');
    }
    
    console.log('MapContext: Initializing visibleLayers:', { defaultVisible, permanentLayers: permanentLayers.map(l => ({ id: l.id, visible: l.visible })) });
    return defaultVisible;
  });

  const toggleLayer = (layerId: string) => {
    setVisibleLayers(prev => {
      const isCurrentlyVisible = prev.includes(layerId);
      
      // Handle commercial zones group - toggle both child layers together
      if (layerId === 'zones-commerciales') {
        const childLayers = ['zone-commerciale-chami', 'zone-commerciale-sfariyatt'];
        
        if (isCurrentlyVisible) {
          // Remove parent and all children
          return prev.filter(id => id !== layerId && !childLayers.includes(id));
        } else {
          // Add parent and all children
          return [...prev, layerId, ...childLayers];
        }
      }
      
      // Handle individual child layers - this should not happen with the new UI
      // but keeping for backward compatibility
      if (layerId === 'zone-commerciale-chami' || layerId === 'zone-commerciale-sfariyatt') {
        if (isCurrentlyVisible) {
          // Remove just this child
          const newLayers = prev.filter(id => id !== layerId);
          // If no children are left, also remove the parent
          const remainingChildren = newLayers.filter(id => 
            id === 'zone-commerciale-chami' || id === 'zone-commerciale-sfariyatt'
          );
          if (remainingChildren.length === 0) {
            return newLayers.filter(id => id !== 'zones-commerciales');
          }
          return newLayers;
        } else {
          // Add this child and ensure parent is also added
          const newLayers = [...prev, layerId];
          if (!newLayers.includes('zones-commerciales')) {
            newLayers.push('zones-commerciales');
          }
          return newLayers;
        }
      }
      
      // Handle regular layers
      return isCurrentlyVisible
        ? prev.filter(id => id !== layerId)
        : [...prev, layerId];
    });
  };

  const value = {
    satelliteOn,
    setSatelliteOn,
    satelliteMode,
    setSatelliteMode,
    basemap,
    setBasemap,
    visibleLayers,
    toggleLayer,
    labelsVisible,
    setLabelsVisible,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

// Create a custom hook for easy context consumption
export const useMap = () => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
};
